import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Mail, Phone, MapPin, Calendar, Users, Clock,
  CheckCircle, XCircle, MessageSquare, BadgeCheck, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminInquiryChat from './AdminInquiryChat';
import { isUnread, markSeen } from '../lib/unread';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInquiries = () => {
  const { adminAuth } = useOutletContext();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [chatInquiryId, setChatInquiryId] = useState(null);

  const fetchInquiries = useCallback(async () => {
    try {
      // Use server-side filtering to exclude visa inquiries
      const response = await fetch(`${API_URL}/api/admin/inquiries?inquiry_type=non-visa`, {
        headers: {
          'Authorization': `Basic ${adminAuth}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setInquiries(data);
      }
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  }, [adminAuth]);

  useEffect(() => {
    fetchInquiries();
    const t = setInterval(fetchInquiries, 15000); // light polling
    return () => clearInterval(t);
  }, [fetchInquiries]);

  const openChat = (inq) => {
    markSeen('adm', inq.id, inq.last_activity);
    setChatInquiryId(inq.id);
    setInquiries((prev) => [...prev]);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries/${id}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`
        }
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchInquiries();
      }
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry and its entire conversation? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/inquiries/${id}`, {
        method: 'DELETE', headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (response.ok) { toast.success('Inquiry deleted'); fetchInquiries(); }
      else toast.error('Failed to delete');
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  // Filter out visa inquiries - they have their own dedicated page (now handled server-side)
  const filteredInquiries = filter === 'all' 
    ? inquiries 
    : inquiries.filter(i => i.status === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Inquiries</h1>
          <p className="text-gray-600">Manage travel requests and leads</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'new', 'contacted', 'converted', 'closed'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
            className={filter === status ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">
                {inquiries.filter(i => i.status === status).length}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Inquiries List */}
      <div className="space-y-4">
        {filteredInquiries.map((inquiry) => {
          const unread = isUnread('adm', inquiry, 'team');
          return (
          <Card key={inquiry.id} className={`hover:shadow-md transition-shadow ${unread ? 'ring-2 ring-amber-400' : ''}`}>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Customer Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="font-bold text-lg">{inquiry.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inquiry.status)}`}>
                      {inquiry.status}
                    </span>
                    {inquiry.inquiry_type === 'visa' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Visa</span>
                    )}
                    {unread && (
                      <span data-testid={`unread-${inquiry.id}`} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500 text-white">New message</span>
                    )}
                    {inquiry.user_id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" title="Submitted by a registered account">
                        <BadgeCheck className="w-3 h-3" /> Account
                      </span>
                    )}
                    {inquiry.last_responder && (
                      <span data-testid={`responder-${inquiry.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700" title="Staff member(s) who replied">
                        <Users className="w-3 h-3" /> Replied by {(inquiry.responders && inquiry.responders.length > 1) ? inquiry.responders.join(', ') : inquiry.last_responder}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${inquiry.email}`} className="hover:text-amber-600">{inquiry.email}</a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${inquiry.phone}`} className="hover:text-amber-600">{inquiry.phone}</a>
                    </div>
                    {inquiry.destination && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{inquiry.destination}</span>
                      </div>
                    )}
                    {inquiry.travel_dates && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{inquiry.travel_dates}</span>
                      </div>
                    )}
                    {inquiry.travelers && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{inquiry.travelers} travelers</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{formatDate(inquiry.created_at)}</span>
                    </div>
                  </div>

                  {inquiry.message && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed min-h-[80px] max-h-[300px] overflow-y-auto">{inquiry.message}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-row lg:flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => openChat(inquiry)}
                    data-testid={`open-chat-${inquiry.id}`}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send Itinerary / Chat
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(inquiry.id, 'contacted')}
                    disabled={inquiry.status === 'contacted'}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Mark Contacted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 border-green-600 hover:bg-green-50"
                    onClick={() => updateStatus(inquiry.id, 'converted')}
                    disabled={inquiry.status === 'converted'}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Converted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-gray-600"
                    onClick={() => updateStatus(inquiry.id, 'closed')}
                    disabled={inquiry.status === 'closed'}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Close
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => deleteInquiry(inquiry.id)}
                    data-testid={`delete-inquiry-${inquiry.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}

        {filteredInquiries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No inquiries found{filter !== 'all' && ` with status "${filter}"`}</p>
          </div>
        )}
      </div>

      {chatInquiryId && (
        <AdminInquiryChat
          inquiryId={chatInquiryId}
          adminAuth={adminAuth}
          onClose={() => setChatInquiryId(null)}
          onSent={fetchInquiries}
        />
      )}
    </div>
  );
};

export default AdminInquiries;
